import {
  MAXIMUM_VIDEO_SELECTIONS,
  MetricRegistry,
  videoLabel,
  type ReportFilterStateV1,
  type ReportMetricId,
  type ReportSort,
  type ReportTotal,
  type VideoMetricRow,
  type VideoSelection,
} from "@meta-video/contracts";
import { ActionIcon, Button, Checkbox, Tooltip } from "@mantine/core";
import { useId, useState } from "react";

import type { ReportApi } from "./api.js";
import {
  isReportMetricColumnId,
  isVideoCoverageMetric,
  reportColumnLabel,
  visibleReportColumnIds,
  type ReportColumnId,
} from "./columns.js";
import { DrilldownRow } from "./DrilldownRow.js";
import {
  formatDuration,
  formatMetricValue,
  formatUtcDate,
  NoDataCell,
} from "./format.js";
import { VideoIdentityCell } from "./VideoIdentityCell.js";
import { StatusBadge } from "../video/StatusBadge.js";

export interface ReportTableProps {
  readonly api: ReportApi;
  readonly columnIds: readonly ReportColumnId[];
  readonly disabled?: boolean;
  readonly expectedSnapshotFingerprint?: string;
  readonly filters: ReportFilterStateV1;
  readonly focusedRegionId?: string;
  readonly focusedVideo: VideoSelection | null;
  readonly onAccessChanged?: () => void;
  readonly onFocusVideo: (selection: VideoSelection) => void;
  readonly onInspectVideo?: (selection: VideoSelection, trigger: HTMLButtonElement) => void;
  readonly onSessionExpired?: () => void;
  readonly onPlay: (row: VideoMetricRow, trigger: HTMLButtonElement) => void;
  readonly onSortChange: (sorts: readonly ReportSort[]) => void;
  readonly onToggleVideo: (selection: VideoSelection) => void;
  readonly rows: readonly VideoMetricRow[];
  readonly selectedVideos: readonly VideoSelection[];
  readonly sorts: readonly ReportSort[];
  readonly total: ReportTotal;
  readonly videoMetricCoverageThreshold: number;
}

export function ReportTable({
  api,
  columnIds,
  disabled = false,
  expectedSnapshotFingerprint,
  filters,
  focusedRegionId,
  focusedVideo,
  onAccessChanged,
  onFocusVideo,
  onInspectVideo,
  onSessionExpired,
  onPlay,
  onSortChange,
  onToggleVideo,
  rows,
  selectedVideos,
  sorts,
  total,
  videoMetricCoverageThreshold,
}: ReportTableProps) {
  const [expandedKeys, setExpandedKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const hideVideoMetrics = total.videoCoverageRate === null
    || total.videoCoverageRate < videoMetricCoverageThreshold;
  const visibleColumns = visibleReportColumnIds(columnIds).filter(
    (columnId) => !(hideVideoMetrics && isVideoCoverageMetric(columnId)),
  );
  const drilldownColumns = visibleColumns.filter(isReportMetricColumnId);

  return (
    <div className="report-table-scroll" role="region" aria-label="Video report table" tabIndex={0}>
      <table className="report-table">
        <thead>
          <tr>
            <th className="report-table__expander" scope="col">
              <span className="visually-hidden">Expand contributing ads</span>
            </th>
            <th className="report-table__selector" scope="col">
              <span className="visually-hidden">Select videos to compare</span>
            </th>
            <th className="report-table__identity" scope="col">Video</th>
            {visibleColumns.map((columnId) => (
              <ReportHeader
                columnId={columnId}
                disabled={disabled}
                key={columnId}
                onSortChange={onSortChange}
                sorts={sorts}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const key = `${row.brandId}\0${row.videoId}`;
            const expanded = expandedKeys.has(key);
            const focused = focusedVideo?.brandId === row.brandId
              && focusedVideo.videoId === row.videoId;
            const selected = selectedVideos.some(
              (selection) => selection.brandId === row.brandId
                && selection.videoId === row.videoId,
            );
            return (
              <ReportBodyRows
                api={api}
                columns={visibleColumns}
                disabled={disabled}
                drilldownColumns={drilldownColumns}
                expanded={expanded}
                expectedSnapshotFingerprint={expectedSnapshotFingerprint}
                filters={filters}
                focused={focused}
                focusedRegionId={focusedRegionId}
                key={key}
                onAccessChanged={onAccessChanged}
                onFocusVideo={onFocusVideo}
                onInspectVideo={onInspectVideo}
                onSessionExpired={onSessionExpired}
                onPlay={onPlay}
                onToggleVideo={onToggleVideo}
                onToggle={() => {
                  setExpandedKeys((current) => {
                    const next = new Set(current);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  });
                }}
                row={row}
                selected={selected}
                selectionDisabled={!selected
                  && selectedVideos.length >= MAXIMUM_VIDEO_SELECTIONS}
                threshold={videoMetricCoverageThreshold}
              />
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <th aria-hidden="true" className="report-table__expander" />
            <th aria-hidden="true" className="report-table__selector" />
            <th className="report-table__identity" scope="row">
              {total.videoCount.toLocaleString("en-GB")} {total.videoCount === 1 ? "video" : "videos"}
            </th>
            {visibleColumns.map((columnId) => (
              <td aria-hidden="true" key={columnId} />
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function ReportHeader({
  columnId,
  disabled,
  onSortChange,
  sorts,
}: {
  readonly columnId: ReportColumnId;
  readonly disabled: boolean;
  readonly onSortChange: (sorts: readonly ReportSort[]) => void;
  readonly sorts: readonly ReportSort[];
}) {
  if (!isReportMetricColumnId(columnId)) {
    return <th aria-sort="none" scope="col">{reportColumnLabel(columnId)}</th>;
  }
  const sortIndex = sorts.findIndex(({ metric }) => metric === columnId);
  const sort = sorts[sortIndex];
  const ariaSort = sortIndex === 0
    ? sort?.direction === "asc" ? "ascending" : "descending"
    : sortIndex > 0 ? "other" : "none";
  const content = (
    <button
      aria-label={`Sort by ${MetricRegistry[columnId].label}`}
      className="report-table__sort"
      disabled={disabled}
      onClick={(event) => onSortChange(nextSorts(sorts, columnId, event.shiftKey))}
      type="button"
    >
      <span>{MetricRegistry[columnId].label}</span>
      {sort ? <span aria-hidden="true">{sort.direction === "asc" ? "↑" : "↓"}{sortIndex + 1}</span> : null}
    </button>
  );
  return (
    <th aria-sort={ariaSort} scope="col">
      {MetricRegistry[columnId].tooltip ? (
        <Tooltip label={MetricRegistry[columnId].tooltip} multiline w={320}>
          {content}
        </Tooltip>
      ) : content}
    </th>
  );
}

export function nextSorts(
  current: readonly ReportSort[],
  metric: ReportMetricId,
  append: boolean,
): readonly ReportSort[] {
  const existingIndex = current.findIndex((sort) => sort.metric === metric);
  if (!append) {
    return [{
      direction: existingIndex === 0 && current[0]?.direction === "asc" ? "desc" : "asc",
      metric,
    }];
  }
  if (existingIndex >= 0) {
    return current.map((sort, index) => index === existingIndex
      ? { ...sort, direction: sort.direction === "asc" ? "desc" : "asc" }
      : sort);
  }
  return [...current.slice(0, 3), { direction: "asc", metric }];
}

function ReportBodyRows({
  api,
  columns,
  disabled,
  drilldownColumns,
  expanded,
  expectedSnapshotFingerprint,
  filters,
  focused,
  focusedRegionId,
  onAccessChanged,
  onFocusVideo,
  onInspectVideo,
  onSessionExpired,
  onPlay,
  onToggleVideo,
  onToggle,
  row,
  selected,
  selectionDisabled,
  threshold,
}: {
  readonly api: ReportApi;
  readonly columns: readonly ReportColumnId[];
  readonly disabled: boolean;
  readonly drilldownColumns: readonly ReportMetricId[];
  readonly expanded: boolean;
  readonly expectedSnapshotFingerprint?: string;
  readonly filters: ReportFilterStateV1;
  readonly focused: boolean;
  readonly focusedRegionId?: string;
  readonly onAccessChanged?: () => void;
  readonly onFocusVideo: (selection: VideoSelection) => void;
  readonly onInspectVideo?: (selection: VideoSelection, trigger: HTMLButtonElement) => void;
  readonly onSessionExpired?: () => void;
  readonly onPlay: (row: VideoMetricRow, trigger: HTMLButtonElement) => void;
  readonly onToggleVideo: (selection: VideoSelection) => void;
  readonly onToggle: () => void;
  readonly row: VideoMetricRow;
  readonly selected: boolean;
  readonly selectionDisabled: boolean;
  readonly threshold: number;
}) {
  const drilldownId = useId();
  const label = videoLabel(row);
  const selection = { brandId: row.brandId, videoId: row.videoId };
  const focusVideo = () => onFocusVideo(selection);
  return (
    <>
      <tr
        aria-controls={focused ? focusedRegionId : undefined}
        aria-current={focused ? "true" : undefined}
        className={focused ? "report-table__row report-table__row--focused" : "report-table__row"}
        data-video-key={`${row.brandId}\0${row.videoId}`}
        onClick={(event) => {
          if (disabled || isNestedRowControl(event.target)) return;
          if (document.activeElement !== event.currentTarget) focusVideo();
        }}
        onFocus={(event) => {
          if (!disabled && event.target === event.currentTarget) focusVideo();
        }}
        onKeyDown={(event) => {
          if (
            disabled
            || event.target !== event.currentTarget
            || (event.key !== "Enter" && event.key !== " ")
          ) return;
          event.preventDefault();
          focusVideo();
        }}
        tabIndex={disabled ? -1 : 0}
      >
        <td className="report-table__expander">
          <ActionIcon
            aria-controls={drilldownId}
            aria-expanded={expanded}
            aria-label={`${expanded ? "Collapse" : "Expand"} ads for ${label.primary}`}
            disabled={disabled}
            onClick={onToggle}
            size="sm"
            variant="subtle"
          >
            <span aria-hidden="true">{expanded ? "−" : "+"}</span>
          </ActionIcon>
        </td>
        <td className="report-table__selector">
          <Checkbox
            aria-label={`${selected ? "Remove" : "Select"} ${label.primary} ${selected ? "from" : "for"} comparison`}
            checked={selected}
            disabled={disabled || selectionDisabled}
            onChange={() => onToggleVideo({ brandId: row.brandId, videoId: row.videoId })}
          />
        </td>
        <th className="report-table__identity" scope="row">
          <VideoIdentityCell
            identity={row}
            secondaryAction={(
              <>
                {row.playStatus === "hosted" ? null : (
                  <StatusBadge status={row.playStatus} />
                )}
                {onInspectVideo ? (
                  <Button
                    aria-label={`Inspect ${label.primary}`}
                    className="report-table__inspect"
                    disabled={disabled}
                    onClick={(event) => onInspectVideo(selection, event.currentTarget)}
                    size="compact-xs"
                    type="button"
                    variant="default"
                  >
                    Inspect
                  </Button>
                ) : row.playStatus === "hosted" ? (
                  <Button
                    aria-label={`Inspect ${label.primary}`}
                    disabled={disabled}
                    onClick={(event) => onPlay(row, event.currentTarget)}
                    size="compact-xs"
                    type="button"
                    variant="light"
                  >
                    Inspect
                  </Button>
                ) : null}
              </>
            )}
          />
        </th>
        {columns.map((columnId) => (
          <RowCell columnId={columnId} key={columnId} row={row} threshold={threshold} />
        ))}
      </tr>
      {expanded ? (
        <tr className="report-table__drilldown">
          <td colSpan={columns.length + 3}>
            <div aria-label={`Contributing ads for ${label.primary}`} id={drilldownId} role="region">
              <DrilldownRow
                api={api}
                columnIds={drilldownColumns}
                expectedFingerprint={expectedSnapshotFingerprint}
                filters={filters}
                onAccessChanged={onAccessChanged}
                onSessionExpired={onSessionExpired}
                row={row}
                videoMetricCoverageThreshold={threshold}
              />
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function isNestedRowControl(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest(
    "button, input, select, textarea, a, [role='button'], [role='checkbox'], [data-row-action]",
  ) !== null;
}

function RowCell({
  columnId,
  row,
  threshold,
}: {
  readonly columnId: ReportColumnId;
  readonly row: VideoMetricRow;
  readonly threshold: number;
}) {
  if (columnId === "duration") {
    const formatted = formatDuration(row.lengthSeconds);
    return (
      <td>
        {formatted ?? <span aria-label="Duration unknown" className="unknown-duration-marker">Unknown</span>}
        {row.lengthSeconds !== null && row.lengthSeconds <= 15 ? (
          <Tooltip label={MetricRegistry.holdRate.tooltip} multiline w={320}>
            <span aria-label="15 seconds or shorter" className="short-video-marker">≤15s</span>
          </Tooltip>
        ) : null}
      </td>
    );
  }
  if (columnId === "firstSeen") {
    return <td>{formatUtcDate(row.firstSeen)}</td>;
  }
  const value = row[columnId];
  const covered = !isVideoCoverageMetric(columnId)
    || (row.videoCoverageRate !== null && row.videoCoverageRate >= threshold);
  const formatted = covered ? formatMetricValue(columnId, value, row.currency) : null;
  return <td>{formatted ?? <NoDataCell />}</td>;
}
